import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { integrationConfigClient } from "@/lib/api/integration-config-client";

/** Mirrors the backend integrations TenantArchiveStatus schema. */
export interface TenantArchiveStatus {
  tenant_id: string;
  is_archived: boolean;
  archived_at?: string | null;
  archived_by?: string | null;
  reason?: string | null;
  credentials_total: number;
  credentials_disabled: number;
}

export interface ArchiveTenantRequest {
  archived_by?: string | null;
  reason?: string | null;
}

// Scope a request to a target tenant. The backoffice-api proxy requires the
// `tenant_id` query param (it injects x-tenant-id and retargets identity from
// it); the integrations service reads the tenant from the path. Sending the
// header too is safe in LOCAL mode.
const tenantScope = (tenantId: string) => ({
  params: { tenant_id: tenantId },
  headers: { "x-tenant-id": tenantId },
});

export const tenantArchiveStatusQueryKey = (tenantId: string) =>
  ["tenant-archive-status", tenantId] as const;

export function useTenantArchiveStatus(tenantId: string, enabled = true) {
  return useQuery({
    queryKey: tenantArchiveStatusQueryKey(tenantId),
    queryFn: async () => {
      const response = await integrationConfigClient.get<TenantArchiveStatus>(
        `/internal/tenants/${tenantId}/archive`,
        tenantScope(tenantId),
      );
      return response.data;
    },
    enabled: enabled && !!tenantId,
  });
}

export function useArchiveTenant(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ArchiveTenantRequest = {}) => {
      const response = await integrationConfigClient.post<TenantArchiveStatus>(
        `/internal/tenants/${tenantId}/archive`,
        body,
        tenantScope(tenantId),
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: tenantArchiveStatusQueryKey(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["integration-credentials", tenantId],
      });
    },
  });
}

export function useUnarchiveTenant(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await integrationConfigClient.post<TenantArchiveStatus>(
        `/internal/tenants/${tenantId}/unarchive`,
        undefined,
        tenantScope(tenantId),
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: tenantArchiveStatusQueryKey(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["integration-credentials", tenantId],
      });
    },
  });
}
